/**
 * 📝 Notas — segundo cerebro con grid visual, búsqueda, scope filter, modal.
 */
import { h, apiClient, pill, emptyState, btn, openModal, openConfirm, toast, sectionHeader, formatDate, openQuickTaskModal } from './_common.js';

const SCOPES = ['global', 'personal', 'autosell', 'inmuebles', 'carlos-ai', 'coco-doree', 'suiza-go'];

let _filter = { scope: '', q: '' };

export async function renderNotas(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' });
  wrap.appendChild(sectionHeader('📝', 'Notas', 'Tu segundo cerebro, organizado por scope', [
    btn('✅ + Tarea', () => openQuickTaskModal('personal'), { variant: 'secondary' }),
    btn('+ Nueva nota', () => openNoteModal(null, () => renderNotas(root))),
  ]));
  // Filtros
  wrap.appendChild(h('div', { class: 'flex gap-2 flex-wrap mb-4' }, [
    h('input', {
      type: 'search', placeholder: '🔍 Buscar notas...', value: _filter.q,
      class: 'flex-1 min-w-[200px] bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm',
      oninput: (e) => { _filter.q = e.target.value; refreshGrid(wrap); },
    }),
    h('select', {
      class: 'bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm',
      onchange: (e) => { _filter.scope = e.target.value; refreshGrid(wrap); },
    }, [
      h('option', { value: '' }, 'Todos los scopes'),
      ...SCOPES.map((s) => h('option', { value: s, ...(s === _filter.scope ? { selected: true } : {}) }, s)),
    ]),
  ]));
  wrap.appendChild(h('div', { id: 'notes-grid', class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' }));
  root.appendChild(wrap);
  await refreshGrid(wrap);
}

async function refreshGrid(wrap) {
  const grid = wrap.querySelector('#notes-grid');
  if (!grid) return;
  grid.innerHTML = '<div class="col-span-full text-center text-ink-400 py-6">Cargando...</div>';
  const res = await apiClient.get('/api/v1/notes?limit=200').catch(() => ({ data: [] }));
  let notes = res.data || [];
  if (_filter.scope) notes = notes.filter((n) => n.scope === _filter.scope);
  if (_filter.q) {
    const q = _filter.q.toLowerCase();
    notes = notes.filter((n) => [n.title, n.content].some((x) => (x || '').toLowerCase().includes(q)));
  }
  grid.innerHTML = '';
  if (notes.length === 0) {
    grid.appendChild(h('div', { class: 'col-span-full' }, emptyState('📝', 'Sin notas', 'Crea tu primera nota con el botón +')));
    return;
  }
  notes.forEach((n) => grid.appendChild(noteCard(n, wrap)));
}

function noteCard(n, wrap) {
  return h('div', {
    class: 'bg-base-800/50 border border-base-700 rounded-xl p-4 hover:border-gold-400/40 transition cursor-pointer group',
    onclick: () => openNoteModal(n, () => refreshGrid(wrap)),
  }, [
    h('div', { class: 'flex items-start justify-between mb-2' }, [
      h('h4', { class: 'font-semibold leading-tight pr-2' }, n.title || '(sin título)'),
      pill(n.scope, 'gold'),
    ]),
    h('p', { class: 'text-sm text-ink-300 line-clamp-4 mb-2' }, (n.content || '').slice(0, 300)),
    h('div', { class: 'flex items-center justify-between mt-2 pt-2 border-t border-base-700/50' }, [
      h('span', { class: 'text-xs text-ink-400' }, formatDate(n.createdAt)),
      h('button', {
        class: 'text-xs text-red-400 opacity-0 group-hover:opacity-100 transition',
        onclick: async (e) => {
          e.stopPropagation();
          const ok = await openConfirm({ title: 'Eliminar nota', message: `"${n.title || 'sin título'}"`, danger: true, icon: '🗑️' });
          if (!ok) return;
          await apiClient.delete(`/api/v1/notes/${n.id}`);
          toast('Nota eliminada', 'success');
          await refreshGrid(wrap);
        },
      }, '🗑'),
    ]),
  ]);
}

function openNoteModal(note, onDone) {
  openModal({
    title: note ? 'Editar nota' : 'Nueva nota',
    icon: '📝',
    size: 'lg',
    fields: [
      { name: 'title', label: 'Título', value: note?.title, required: true, placeholder: 'p.ej. Ideas para Autosell' },
      { name: 'scope', label: 'Scope', type: 'select', value: note?.scope || 'global', options: SCOPES },
      { name: 'content', label: 'Contenido (markdown)', type: 'textarea', value: note?.content, rows: 10, placeholder: '# Tu nota aquí...' },
    ],
    onSubmit: async (vals) => {
      if (note?.id) {
        await apiClient.patch(`/api/v1/notes/${note.id}`, vals);
        toast('Nota actualizada', 'success');
      } else {
        await apiClient.post('/api/v1/notes', vals);
        toast('Nota creada', 'success');
      }
      onDone();
    },
  });
}
